import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
const MAX_CARDS = 4;
const MAX_SOURCE_ITEMS = 3;
const MAESTRA_CARD_SECTION_ID = 'maestra-cart-rec';
const MAESTRA_CACHE_KEY = 'maestra-cart-recs';
const MAESTRA_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

/**
 * Runs `callback` once the browser has painted.
 *
 * This panel can fire up to eight product-page renders in one go. Started
 * straight off the cart update they compete with the drawer's own first paint
 * for a mobile connection's few parallel slots, so the customer waits on
 * requests for products they have not asked for yet. Two frames is enough to
 * let the drawer paint first; nothing here is above the fold.
 *
 * @param {() => void} callback
 */
function afterPaint(callback) {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

/**
 * Desktop-only complementary products panel for the cart drawer.
 *
 * Recommendation source is tiered:
 *   1. Maestra personal recommendations — device-identified, fetched once per
 *      session (sessionStorage cache), cards rendered via the Section
 *      Rendering API so markup matches the theme's own.
 *   2. Fallback: Shopify complementary recommendations for up to the first
 *      MAX_SOURCE_ITEMS cart items, merged round-robin, with a further
 *      fallback to 'related' intent.
 *
 * Owns its own DOM (no data-hydration-key, so drawer hydration morphs never
 * touch it). Loads when the drawer opens on a >= 990px viewport — mobile never
 * makes a request.
 */
class CartRecommendationsComponent extends Component {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {AbortController | null} */
  #activeBatch = null;

  /** @type {string | null} */
  #renderedKey = null;

  /** @type {string[] | null} */
  #pendingProductIds = null;

  /** @type {'idle' | 'ok' | 'failed'} 'failed' means don't retry this page load. */
  #maestraState = 'idle';

  /** @type {HTMLElement[]} Pool of Maestra cards (may exceed MAX_CARDS, filtered against cart at render). */
  #maestraCards = [];

  /** @type {Set<string>} */
  #cartProductIds = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));

  /** @type {MediaQueryList} */
  #mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

  /** @type {Element | null} */
  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    this.#mediaQuery.addEventListener('change', this.#onDrawerOpen);

    // Restore path: the drawer may already be open before this module loads.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#onDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    this.#mediaQuery.removeEventListener('change', this.#onDrawerOpen);
    this.#activeBatch?.abort();
  }

  /** @returns {boolean} */
  get #isDrawerOpen() {
    return this.#themeDrawer?.hasAttribute('open') ?? false;
  }

  /** @returns {string[]} */
  #idsFromDataset() {
    return (this.dataset.productIds || '').split(',').filter(Boolean);
  }

  #onDrawerOpen = () => {
    if (!this.#isDrawerOpen) return;
    afterPaint(() => {
      if (!this.#isDrawerOpen) return;
      this.#load(this.#pendingProductIds ?? this.#idsFromDataset());
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then((result) => {
        const detail = /** @type {any} */ (result)?.detail;
        if (detail?.didError) return;

        const items = detail?.items ?? detail?.resource?.items ?? detail?.cart?.items;
        if (!Array.isArray(items)) {
          console.warn('[cart-recommendations] Unrecognised cart update payload:', detail);
          return;
        }

        this.#cartProductIds = new Set(items.map((item) => String(item.product_id)));

        const leadIds = items.slice(0, MAX_SOURCE_ITEMS).map((item) => String(item.product_id));
        if (leadIds.length === 0) return;

        if (this.#isDrawerOpen) {
          afterPaint(() => this.#load(leadIds));
        } else {
          this.#pendingProductIds = leadIds;
        }
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-recommendations] Event promise rejected:', error);
      });
  };

  /**
   * @param {string[]} productIds
   */
  async #load(productIds) {
    if (!this.#mediaQuery.matches) return;

    // Tier 1: Maestra personal recommendations.
    if (this.#maestraState !== 'failed') {
      const rendered = await this.#tryMaestra();
      if (rendered) return;
    }

    // Tier 2: original Shopify complementary/related flow.
    const { url: baseUrl, sectionId, intent } = this.dataset;

    if (!productIds.length || !baseUrl || !sectionId) return;

    const key = productIds.join(',');
    if (key === this.#renderedKey) {
      // Same lead items, but the cart set may have changed — drop any
      // rendered cards for products now in the cart.
      const list = this.querySelector('.cart-recommendations__list');
      if (list) this.#pruneCardsInCart(list);
      return;
    }

    this.#activeBatch?.abort();
    const batch = new AbortController();
    this.#activeBatch = batch;

    try {
      const fetchAll = (/** @type {string} */ intentValue) =>
        Promise.all(
          productIds.map((productId) =>
            this.#fetchCached(
              `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intentValue}`,
              batch.signal
            )
          )
        );

      let responses = await fetchAll(intent ?? 'complementary');
      if (batch.signal.aborted) return;

      let cards = this.#mergeCards(responses);

      // No complementary products configured for these items — fall back to
      // algorithmic related products instead of leaving stale content.
      if (cards.length === 0 && intent === 'complementary') {
        responses = await fetchAll('related');
        if (batch.signal.aborted) return;
        cards = this.#mergeCards(responses);
      }
      const list = this.querySelector('.cart-recommendations__list');
      if (!list) return;

      if (cards.length > 0) {
        list.replaceChildren(...cards);
      } else {
        // No complements for the current lead items — keep the last good
        // cards, minus anything that's since been added to the cart.
        this.#pruneCardsInCart(list);
      }

      this.#renderedKey = key;
      this.#pendingProductIds = null;
    } catch (error) {
      if (/** @type {any} */ (error)?.name === 'AbortError') return;
      console.warn('[cart-recommendations]', error);
    }
  }

  /**
   * Attempts to render Maestra personal recommendations. The card pool is
   * cached in sessionStorage (TTL-bound), so across a session Maestra and
   * the card-rendering fetches are only hit once; subsequent pages restore
   * the pool and re-render it filtered against the current cart.
   *
   * @returns {Promise<boolean>} true if the panel was rendered from Maestra.
   */
  async #tryMaestra() {
    if (this.#maestraState === 'ok') {
      return this.#renderMaestraCards();
    }

    // Session cache: restore the pool without any network.
    try {
      const cached = JSON.parse(sessionStorage.getItem(MAESTRA_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.ts < MAESTRA_CACHE_TTL && Array.isArray(cached.cards) && cached.cards.length) {
        const template = document.createElement('template');
        this.#maestraCards = cached.cards
          .map((/** @type {string} */ html) => {
            template.innerHTML = html;
            return /** @type {HTMLElement | null} */ (template.content.firstElementChild);
          })
          .filter((/** @type {HTMLElement | null} */ card) => card !== null);

        if (this.#maestraCards.length > 0) {
          this.#maestraState = 'ok';
          const rendered = this.#renderMaestraCards();
          if (rendered) {
            console.log(`[cart-recommendations] ✅ Using Maestra personalised recommendations (${this.#maestraCards.length} in pool, session cache)`);
          }
          return rendered;
        }
      }
    } catch {
      // Corrupt/unavailable storage — fall through to a fresh fetch.
    }

    try {
      const { maestraEndpointId, maestraOperation } = this.dataset;
      if (!maestraEndpointId || !maestraOperation) throw new Error('Maestra not configured');

      const deviceUUID = document.cookie.match(/maestraDeviceUUID=([^;]+)/)?.[1];
      if (!deviceUUID) throw new Error('no maestraDeviceUUID cookie');

      const limit = Number(this.dataset.maestraLimit) || 8;
      const response = await fetch('https://api.maestra.io/v3/js/operations/sync', {
        method: 'POST',
        body: new URLSearchParams({
          endpointId: maestraEndpointId,
          operation: maestraOperation,
          deviceUUID,
          originDomain: location.hostname,
          data: JSON.stringify({ recommendation: { limit } }),
        }),
      });
      if (!response.ok) throw new Error(`Maestra HTTP ${response.status}`);

      const data = await response.json();
      if (data.status !== 'Success') throw new Error(`Maestra status: ${data.status}`);

      const recs = data.recommendations || [];
      if (recs.length === 0) throw new Error('Maestra returned no recommendations');

      // Render each recommended product's card through the theme's own
      // partial via the Section Rendering API, so markup/forms are identical.
      const htmls = await Promise.all(
        recs.map((/** @type {any} */ rec) => {
          const u = new URL(rec.url);
          const sep = u.search ? '&' : '?';
          return fetch(`${u.pathname}${u.search}${sep}section_id=${MAESTRA_CARD_SECTION_ID}`, { priority: 'low' })
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '');
        })
      );

      const parser = new DOMParser();
      this.#maestraCards = htmls
        .filter(Boolean)
        .map((html) =>
          /** @type {HTMLElement | null} */ (
            parser.parseFromString(html, 'text/html').querySelector('li.cart-addon-card[data-product-id]')
          )
        )
        .filter((card) => card !== null)
        .map((card) => /** @type {HTMLElement} */ (document.importNode(card, true)));

      if (this.#maestraCards.length === 0) throw new Error('no Maestra cards could be rendered');

      try {
        sessionStorage.setItem(
          MAESTRA_CACHE_KEY,
          JSON.stringify({ ts: Date.now(), cards: this.#maestraCards.map((card) => card.outerHTML) })
        );
      } catch {
        // Storage full/unavailable — caching is best-effort.
      }

      this.#maestraState = 'ok';
      const rendered = this.#renderMaestraCards();
      if (rendered) {
        console.log(`[cart-recommendations] ✅ Using Maestra personalised recommendations (${this.#maestraCards.length} in pool)`);
      }
      return rendered;
    } catch (error) {
      this.#maestraState = 'failed';
      console.log(`[cart-recommendations] ↩️ Falling back to theme recommendations — ${/** @type {any} */ (error)?.message}`);
      return false;
    }
  }

  /**
   * Renders up to MAX_CARDS from the Maestra pool, skipping products
   * currently in the cart. Returns false if nothing renderable remains
   * (caller then falls through to the Shopify flow).
   *
   * @returns {boolean}
   */
  #renderMaestraCards() {
    const list = this.querySelector('.cart-recommendations__list');
    if (!list) return false;

    /** @type {HTMLElement[]} */
    const cards = [];
    const seen = new Set();
    for (const card of this.#maestraCards) {
      if (cards.length >= MAX_CARDS) break;
      const productId = card.dataset.productId;
      if (!productId || seen.has(productId) || this.#cartProductIds.has(productId)) continue;
      seen.add(productId);
      cards.push(/** @type {HTMLElement} */ (document.importNode(card, true)));
    }

    if (cards.length === 0) return false;

    list.replaceChildren(...cards);
    return true;
  }

  /**
   * @param {string} url
   * @param {AbortSignal} signal
   * @returns {Promise<string>} The response HTML, or an empty string on failure.
   */
  async #fetchCached(url, signal) {
    const cached = this.#cache.get(url);
    if (cached !== undefined) return cached;

    try {
      const response = await fetch(url, { signal, priority: 'low' });
      if (!response.ok) return '';

      const html = await response.text();
      this.#cache.set(url, html);
      return html;
    } catch (error) {
      if (/** @type {any} */ (error)?.name === 'AbortError') throw error;
      return '';
    }
  }

  /**
   * Removes cards for products now in the cart from the live list.
   * @param {Element} list
   */
  #pruneCardsInCart(list) {
    const cards = /** @type {NodeListOf<HTMLElement>} */ (
      list.querySelectorAll('.cart-addon-card[data-product-id]')
    );
    for (const card of cards) {
      if (card.dataset.productId && this.#cartProductIds.has(card.dataset.productId)) {
        card.remove();
      }
    }
  }

  /**
   * Round-robin merge of cards across responses, deduped by product id,
   * skipping products now in the cart, capped at MAX_CARDS.
   *
   * @param {string[]} responses
   * @returns {HTMLElement[]}
   */
  #mergeCards(responses) {
    const parser = new DOMParser();
    const sources = responses
      .filter(Boolean)
      .map((html) => /** @type {HTMLElement[]} */ ([
        ...parser.parseFromString(html, 'text/html').querySelectorAll('li.cart-addon-card[data-product-id]'),
      ]));

    /** @type {HTMLElement[]} */
    const merged = [];
    const seen = new Set();
    const longest = Math.max(0, ...sources.map((cards) => cards.length));

    for (let index = 0; index < longest && merged.length < MAX_CARDS; index++) {
      for (const cards of sources) {
        if (merged.length >= MAX_CARDS) break;

        const card = cards[index];
        const productId = card?.dataset.productId;
        if (!productId || seen.has(productId) || this.#cartProductIds.has(productId)) continue;

        seen.add(productId);
        merged.push(/** @type {HTMLElement} */ (document.importNode(card, true)));
      }
    }

    return merged;
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}